import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiErrorMessage } from "../api/client";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useWorkspaceEvents, type WorkspaceEvent } from "../hooks/useWorkspaceEvents";
import { chatApi, type AccessCardView, type ChatMessage, type SendBody } from "./api";
import { Composer, type Draft } from "./Composer";
import { ForkCard } from "./ForkCard";
import { ResultCard } from "./ResultCard";
import { LibraryCard, type LibraryMeta } from "./cards/LibraryCard";
import { SpecCard, type SpecCardMeta } from "./cards/SpecCard";
import { AccessCard } from "./cards/AccessCard";
import { fileHref, UI, useLang, arabicCount, arabicDigits, DECISIONS_AR } from "./lang";
import { Loading } from "./Loading";

const ROLE: Record<string, { en: string; ar: string }> = {
  cxo: { en: "the Supervisor", ar: "المشرف" },
  operator: { en: "a Team member", ar: "عضو الفريق" },
  reviewer: { en: "the Compliance reviewer", ar: "مراجع الامتثال" },
  agent_handler: { en: "the Agent handler", ar: "مسؤول الوكلاء" },
  approver: { en: "the Approver", ar: "المعتمد" },
};

type Choice = { hand_key: string; en: string; ar: string };
const EMPTY: Draft = { text: "", chip: null, attachment: null };

/** Past this, a working line is treated as lost (a restart mid-run), not as working. */
const STALE_MS = 15 * 60_000;

/**
 * A working line is open until its own finishing line arrives — paired by
 * spawn id, so two runs of the same agent never close each other — and for
 * no longer than STALE_MS.
 */
/** Working lines whose run has finished: the result says what happened, so they are not shown. */
export function finishedWorking(messages: ChatMessage[]): Set<string> {
  const done = new Set<string>();
  messages.forEach((m, i) => {
    if (m.kind !== "system" || m.meta["phase"] !== "working") return;
    const id = m.meta["spawn_id"];
    const hand = m.meta["hand_key"];
    if (
      messages
        .slice(i + 1)
        .some((n) => n.meta["phase"] === "finished" && (id ? n.meta["spawn_id"] === id : n.meta["hand_key"] === hand))
    ) {
      done.add(m.id);
    }
  });
  return done;
}

function openWorking(messages: ChatMessage[], now: number): Set<string> {
  const open = new Set<string>();
  messages.forEach((m, i) => {
    if (m.kind !== "system" || m.meta["phase"] !== "working") return;
    const started = Date.parse(String(m.meta["started_at"] ?? ""));
    if (Number.isFinite(started) && now - started > STALE_MS) return;
    const id = m.meta["spawn_id"];
    const hand = m.meta["hand_key"];
    const finished = messages
      .slice(i + 1)
      .some((n) =>
        n.meta["phase"] === "finished" && (id ? n.meta["spawn_id"] === id : n.meta["hand_key"] === hand),
      );
    if (!finished) open.add(m.id);
  });
  return open;
}

/** A quiet gap this long is a session break (as the API's currentSession). */
const SESSION_GAP_MS = 30 * 60_000;

function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** A day heading, a quiet-gap rule, or a New topic rule — before message vi. */
function dividerFor(list: ChatMessage[], vi: number): React.ReactNode {
  const m = list[vi]!;
  const prev = vi > 0 ? list[vi - 1] : undefined;
  if (m.meta["topic_break"] === true) {
    return (
      <div className="pwa-divider topic" data-testid="divider-topic">
        <DividerText en="New topic" ar="موضوع جديد" />
      </div>
    );
  }
  if (!prev || dayKey(prev.created_at) !== dayKey(m.created_at)) {
    const today = dayKey(new Date().toISOString()) === dayKey(m.created_at);
    const yesterday = dayKey(new Date(Date.now() - 86_400_000).toISOString()) === dayKey(m.created_at);
    const d = new Date(m.created_at);
    return (
      <div className="pwa-divider" data-testid={today ? "divider-today" : "divider-day"}>
        {today ? (
          <DividerText en="Today" ar="اليوم" />
        ) : yesterday ? (
          <DividerText en="Yesterday" ar="أمس" />
        ) : (
          <span>{d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}</span>
        )}
      </div>
    );
  }
  if (prev.meta["topic_break"] !== true && Date.parse(m.created_at) - Date.parse(prev.created_at) > SESSION_GAP_MS) {
    const d = new Date(m.created_at);
    return (
      <div className="pwa-divider gap" data-testid="divider-gap">
        <span>{d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
      </div>
    );
  }
  return null;
}

function DividerText({ en, ar }: { en: string; ar: string }) {
  const { t } = useLang();
  return <span>{t({ en, ar })}</span>;
}

/** Where a run is, in words (the run's own stage names). */
const STAGE_WORDS: Record<string, { en: string; ar: string }> = {
  load_inputs: { en: "Reading its inputs", ar: "قراءة المدخلات" },
  resolve_rules: { en: "Fetching the rules", ar: "جلب القواعد" },
  calculate: { en: "Calculating", ar: "الحساب" },
  pre_gate: { en: "Checking the rules", ar: "التحقق من القواعد" },
  model: { en: "Drafting", ar: "الصياغة" },
  post_gate: { en: "Checking the draft", ar: "مراجعة المسودة" },
};

/** A recorded decision, said in the reader's language. */
const DECISION: Record<string, { en: string; ar: string }> = {
  approved: { en: "approved", ar: "تمت الموافقة" },
  rejected: { en: "rejected", ar: "مرفوض" },
  needs_more_info: { en: "needs more information", ar: "يحتاج إلى مزيد من المعلومات" },
};

/**
 * The pending bubble steps aside once the thread has the line: a poll or the
 * socket can bring the server's copy in while the send is still open, and
 * one line must never show twice.
 */
function alreadyInThread(messages: ChatMessage[], text: string): boolean {
  const last = [...messages].reverse().find((m) => m.kind === "user");
  return !!last && last.body_en === text && Date.now() - Date.parse(last.created_at) < 120_000;
}

export function Thread() {
  const { workspaceId = "" } = useParams<{ workspaceId: string }>();
  const { t, lang } = useLang();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [now, setNow] = useState(() => Date.now());
  const bottom = useRef<HTMLDivElement>(null);

  const queue = useQuery({
    queryKey: ["chat-queue", workspaceId],
    queryFn: () => chatApi.queue(workspaceId),
    enabled: !!workspaceId,
    refetchInterval: 30_000,
  });
  const q = useQuery({
    queryKey: ["chat-thread", workspaceId],
    queryFn: () => chatApi.thread(workspaceId),
    enabled: !!workspaceId,
  });
  const messages = useMemo(() => q.data?.messages ?? [], [q.data]);
  // Sessions inside the one thread: earlier days fold away until asked for.
  const [showEarlier, setShowEarlier] = useState(false);
  const foldFrom = useMemo(() => {
    const last = messages[messages.length - 1];
    if (!last) return 0;
    const day = dayKey(last.created_at);
    const i = messages.findIndex((m) => dayKey(m.created_at) === day);
    return i < 0 ? 0 : i;
  }, [messages]);
  const visible = showEarlier ? messages : messages.slice(foldFrom);
  const hiddenCount = showEarlier ? 0 : foldFrom;
  // Recomputed each tick, so a line past the stale limit stops polling.
  const working = useMemo(() => openWorking(messages, now), [messages, now]);
  const superseded = useMemo(() => finishedWorking(messages), [messages]);
  const [openSources, setOpenSources] = useState<Set<string>>(new Set());
  const [pinIndex, setPinIndex] = useState(0);
  const [pinOpen, setPinOpen] = useState(false);

  // Never silent: a run in progress is polled as well as pushed, so a phone
  // that lost its socket still sees the answer arrive.
  // Keyed on whether anything is open, not on the set: the set is rebuilt
  // every tick, and depending on it tore the 4 s poll down every second so
  // it never fired (found by e2e, pinned by a real-time unit test).
  const anyOpen = working.size > 0;
  useEffect(() => {
    if (!anyOpen) return;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const poll = setInterval(() => void qc.invalidateQueries({ queryKey: ["chat-thread", workspaceId] }), 4000);
    return () => {
      clearInterval(tick);
      clearInterval(poll);
    };
  }, [anyOpen, qc, workspaceId]);

  const onEvent = useCallback(
    (e: WorkspaceEvent) => {
      if (e.type === "chat.message" || e.type === "fork.resolved" || e.type === "fork.opened") {
        void qc.invalidateQueries({ queryKey: ["chat-thread", workspaceId] });
        void qc.invalidateQueries({ queryKey: ["chat-queue", workspaceId] });
      }
    },
    [qc, workspaceId],
  );
  useWorkspaceEvents(workspaceId, onEvent);

  useEffect(() => {
    bottom.current?.scrollIntoView?.({ block: "end" });
  }, [messages.length]);

  const last = messages[messages.length - 1];
  const ask = last?.kind === "system" ? (last.meta["ask"] as { hand_key: string } | undefined) : undefined;

  const send = useMutation({
    mutationFn: (body: SendBody) => chatApi.send(workspaceId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["chat-thread", workspaceId] }),
  });

  const submit = () => {
    const body: SendBody = { text: draft.text.trim() };
    if (draft.attachment) body.attachment_id = draft.attachment.id;
    else if (draft.chip) body.chip = draft.chip;
    else if (ask) body.answer_for = ask.hand_key;
    send.mutate(body);
    setDraft(EMPTY);
  };

  if (!q.data) return <Loading q={q} />;
  const me = q.data.me;
  // Pinned decisions come from the forks themselves (NEW-UX-5: the fork row
  // is the source of truth, the card a view) — so a decision still pins for
  // an approver who joined after it was raised, or for a scheduled run that
  // posted no line to anyone's thread. Thread fork lines add any the queue
  // has not caught up with yet.
  const pinned = new Map<string, NonNullable<ChatMessage["run"]>>();
  const batchFirst = new Map<string, string>();
  const batchSize = new Map<string, number>();
  for (const f of queue.data?.forks ?? []) {
    const run = queue.data?.runs?.[f.run_id];
    // A rewrite is decided on the card of the ad it replaces, never on its own.
    if (!f.resolution && run && f.hand_key !== "h06_rewrite") pinned.set(f.run_id, { ...run, fork: run.fork ?? f });
  }
  for (const m of messages) {
    const f = m.run?.fork;
    if (m.kind === "fork" && m.run && f && !f.resolution && f.approver_role === me.role && !pinned.has(m.run.run_id)) pinned.set(m.run.run_id, m.run);
    const b = f?.resolution?.batch_id;
    if (m.kind === "fork" && b) {
      if (!batchFirst.has(b)) batchFirst.set(b, m.id);
      batchSize.set(b, (batchSize.get(b) ?? 0) + 1);
    }
  }
  // A prompt, not a chip's words: those read as text already typed (usability review).
  // A compose understanding takes corrections in words; any other question is its own prompt.
  const placeholder = ask?.hand_key.startsWith("cs:")
    ? t({ en: "Or tell me what to change…", ar: "أو أخبرني بما تريد تغييره…" })
    : ask && last ? t({ en: last.body_en, ar: last.body_ar }) : t({ en: "Ask for a report or a check…", ar: "اطلب تقريراً أو فحصاً…" });

  return (
    <div className="pwa-thread">
      <div className="pwa-scroll" data-testid="thread">
        {hiddenCount > 0 ? (
          <button type="button" className="pwa-earlier" data-testid="show-earlier" onClick={() => setShowEarlier(true)}>
            {t({ en: `Show earlier (${hiddenCount})`, ar: `عرض الأقدم (${hiddenCount})` })}
          </button>
        ) : null}
        {visible.map((m, vi) => {
          const body = (() => {
          if (m.meta["topic_break"] === true) return null;
          const line = { en: m.body_en, ar: m.body_ar };
          if (m.kind === "user") {
            return (
              <div key={m.id} className="pwa-line user">
                <bdi>{m.body_en}</bdi>
              </div>
            );
          }
          // A run that stopped at the gate is still a result: its figures, what
          // it needs and who has it now, on a card (demo-ready Task 7).
          if ((m.kind === "result" || (m.kind === "system" && m.meta["phase"] === "finished" && m.run?.verdict === "blocked_pre")) && m.run) {
            return <ResultCard key={m.id} run={m.run} line={line} workspaceId={workspaceId} />;
          }
          if (m.kind === "fork" && m.run?.fork) {
            const f = m.run.fork;
            // A group decided with one reason is said once, with its size.
            const batch = f.resolution?.batch_id ?? null;
            if (batch && batchFirst.get(batch) !== m.id) return null;
            const together = batch ? (batchSize.get(batch) ?? 1) : 1;
            // An English reason inside an Arabic line keeps its own direction (bdi).
            const text = f.resolution
              ? <>{`${t(UI.decided)}${together > 1 ? t({ en: ` (${together} together)`, ar: ` (${arabicDigits(together)} معاً)` }) : ""}: ${t(DECISION[f.resolution.decision] ?? { en: f.resolution.decision.replace(/_/g, " "), ar: f.resolution.decision })} — `}<bdi>{`“${f.resolution.reason}”`}</bdi></>
              : pinned.has(m.run.run_id)
                ? t(line)
                : t({
                    en: `Waiting on ${ROLE[f.approver_role]?.en ?? f.approver_role}.`,
                    ar: `بانتظار ${ROLE[f.approver_role]?.ar ?? f.approver_role}.`,
                  });
            return (
              <div key={m.id} className="pwa-line system">
                {text}{" "}
                <Link to={`/app/${workspaceId}/runs/${m.run.run_id}`}>{t(UI.howMade)}</Link>
              </div>
            );
          }
          // The library, and a new agent's spec card (agent-library Task 7).
          if (m.meta["library"]) {
            return (
              <Fragment key={m.id}>
                <div className="pwa-line system">{t(line)}</div>
                <LibraryCard workspaceId={workspaceId} library={m.meta["library"] as LibraryMeta} />
              </Fragment>
            );
          }
          if (m.meta["spec_card"]) {
            return (
              <Fragment key={m.id}>
                <div className="pwa-line system">{t(line)}</div>
                <SpecCard workspaceId={workspaceId} card={m.meta["spec_card"] as SpecCardMeta} />
              </Fragment>
            );
          }
          // A new agent's access, put to this Supervisor (agent-library Task 8).
          if (m.meta["access_request"]) {
            return (
              <Fragment key={m.id}>
                <div className="pwa-line system">{t(line)}</div>
                <AccessCard workspaceId={workspaceId} card={m.meta["access_request"] as AccessCardView} />
              </Fragment>
            );
          }
          if (superseded.has(m.id)) return null;
          const started = typeof m.meta["started_at"] === "string" ? Date.parse(m.meta["started_at"] as string) : NaN;
          const secs = Math.floor((now - started) / 1000);
          const confirm = m.meta["confirm"] as Record<string, unknown> | undefined;
          const choices = m.meta["choices"] as Choice[] | undefined;
          // "Send me this as …": the file the reply carries (agent documents Task 7).
          const files = m.meta["files"] as { format: "pptx" | "xlsx" | "docx" | "pdf"; url: string }[] | undefined;
          const sources = m.kind === "assistant" ? ((m.meta["sources"] as { en: string; ar: string }[] | undefined) ?? []) : [];
          return (
            <div
              key={m.id}
              className={`pwa-line ${m.kind === "assistant" ? "assistant" : "system"}`}
              {...(m.kind === "assistant" ? { "data-testid": `answer-${m.id}` } : {})}
            >
              {t(line)}
              {m.kind === "assistant" && typeof m.meta["answer_run_id"] === "string" ? (
                <>
                  {" "}
                  <Link className="pwa-answer-how" to={`/app/${workspaceId}/runs/${m.meta["answer_run_id"] as string}`}>
                    {t(UI.howMade)}
                  </Link>
                </>
              ) : null}
              {sources.length > 0 ? (
                <div className="pwa-sources">
                  <button
                    type="button"
                    className="pwa-sources-toggle"
                    data-testid={`sources-${m.id}`}
                    aria-expanded={openSources.has(m.id)}
                    onClick={() => {
                      const next = new Set(openSources);
                      if (next.has(m.id)) next.delete(m.id);
                      else next.add(m.id);
                      setOpenSources(next);
                    }}
                  >
                    {t(UI.sources)}
                  </button>
                  {openSources.has(m.id) ? (
                    <ul>
                      {sources.map((src) => (
                        <li key={src.en}>{t(src)}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
              {/* The stage it is in, as it moves, and the time once it runs long
                  (NEW-UX-6, audit fix 8). */}
              {working.has(m.id) && typeof m.meta["stage"] === "string" && STAGE_WORDS[m.meta["stage"] as string] ? (
                <span className="when" data-testid="stage">
                  {t(STAGE_WORDS[m.meta["stage"] as string]!)}
                </span>
              ) : null}
              {working.has(m.id) && secs >= 20 ? (
                <span className="when" data-testid="elapsed">
                  {secs}
                  {t(UI.seconds)}
                  {typeof m.meta["stage"] === "string" ? null : <> · {t(UI.stillWorking)}</>}
                </span>
              ) : null}
              {m.run ? (
                <>
                  {" "}
                  <Link to={`/app/${workspaceId}/runs/${m.run.run_id}`}>{t(UI.howMade)}</Link>
                </>
              ) : null}
              {confirm && m === last && !dismissed.has(m.id) ? (
                <div className="pwa-choices">
                  <button
                    type="button"
                    className="pwa-btn primary"
                    data-testid={`confirm-${m.id}`}
                    disabled={send.isPending}
                    onClick={() => {
                      if (send.isPending) return;
                      setDismissed(new Set([...dismissed, m.id]));
                      send.mutate({ text: t(UI.goAhead), confirm_message_id: m.id });
                    }}
                  >
                    {/* Names what it starts, from the server's catalogue — not the model's words. */}
                    {confirm["label"] && typeof confirm["label"] === "object"
                      ? `${t(UI.goAhead)} — ${t(confirm["label"] as { en: string; ar: string })}`
                      : t(UI.goAhead)}
                  </button>
                  <button
                    type="button"
                    className="pwa-btn"
                    onClick={() => setDismissed(new Set([...dismissed, m.id]))}
                  >
                    {t(UI.notNow)}
                  </button>
                </div>
              ) : null}
              {files && files.length > 0 ? (
                <div className="pwa-links">
                  {files.map((f) => <a key={f.url} href={fileHref(f.url, lang)}>{t(UI[`file_${f.format}`])}</a>)}
                </div>
              ) : null}
              {choices && choices.length > 0 ? (
                <div className="pwa-choices">
                  {choices.map((c) => (
                    <button
                      key={c.hand_key}
                      type="button"
                      className="pwa-btn"
                      // A reply's choice answers its question: one tap sends it.
                      disabled={send.isPending}
                      onClick={() => send.mutate({ text: t(c), chip: c.hand_key })}
                    >
                      {t(c)}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          );
          })();
          return (
            <Fragment key={m.id}>
              {dividerFor(visible, vi)}
              {body}
            </Fragment>
          );
        })}
        {/* What was just sent, at once, and that it is being worked out — never
            a blank pause while the server decides (audit fix 5, NEW-UX-4). */}
        {send.isPending && send.variables && !send.variables.confirm_message_id && !alreadyInThread(messages, send.variables.text) ? (
          <>
            <div className="pwa-line user" data-testid="pending-user">
              {send.variables.text}
            </div>
            <div className="pwa-line system" data-testid="pending-working">
              {t(UI.workingOut)}
            </div>
          </>
        ) : null}
        <div ref={bottom} />
      </div>
      {pinned.size > 0 ? (
        // One decision at a time, with a count and a way through them, capped
        // in height so the thread and the composer always stay in view
        // (audit fix 2). Still pinned: it cannot be scrolled away (NEW-UX-5).
        // Closed, decisions are one line above the composer so the thread
        // stays readable; opened, one card at a time with a way through them.
        // Still pinned either way: never scrolled away (NEW-UX-5).
        <div className={`pwa-pinned${pinOpen ? " open" : ""}`} data-testid="pinned">
          {(() => {
            const list = [...pinned.values()];
            const idx = Math.min(pinIndex, list.length - 1);
            const r = list[idx]!;
            const name = t({ en: r.fork!.hand_name, ar: r.fork!.hand_name_ar ?? r.fork!.hand_name });
            if (!pinOpen) {
              return (
                <div className="pwa-pinned-bar closed" data-testid="pinned-bar">
                  <span className="pwa-pinned-dot" aria-hidden="true" />
                  <span className="pwa-pinned-what">
                    {t({
                      en: `${list.length} ${list.length === 1 ? "decision" : "decisions"} waiting`,
                      ar: `${arabicCount(list.length, DECISIONS_AR)} بانتظارك`,
                    })}
                    <span className="pwa-pinned-next"> · {name}</span>
                  </span>
                  <button type="button" className="pwa-btn primary" data-testid="pinned-open" onClick={() => setPinOpen(true)}>
                    {t({ en: "Review", ar: "مراجعة" })}
                  </button>
                </div>
              );
            }
            return (
              <>
                <div className="pwa-pinned-bar">
                  <span data-testid="pinned-count">
                    {t({ en: `${idx + 1} of ${list.length} decisions`, ar: `${arabicDigits(idx + 1)} من ${arabicDigits(list.length)}` })}
                  </span>
                  <span className="pwa-spacer" />
                  {list.length > 1 ? (
                    <>
                      <button type="button" className="pwa-btn" data-testid="pinned-prev" aria-label="Previous"
                        disabled={idx === 0} onClick={() => setPinIndex(Math.max(0, idx - 1))}>‹</button>
                      <button type="button" className="pwa-btn" data-testid="pinned-next" aria-label="Next"
                        disabled={idx >= list.length - 1} onClick={() => setPinIndex(Math.min(list.length - 1, idx + 1))}>›</button>
                    </>
                  ) : null}
                  <Link className="pwa-pinned-all" to={`/app/${workspaceId}/queue`}>{t(UI.queue)}</Link>
                  <button type="button" className="pwa-btn" data-testid="pinned-close" aria-label={t({ en: "Close", ar: "إغلاق" })}
                    onClick={() => setPinOpen(false)}>×</button>
                </div>
                <ForkCard key={r.run_id} fork={r.fork!} run={r} workspaceId={workspaceId} />
              </>
            );
          })()}
        </div>
      ) : null}
      {send.isError ? (
        <div className="pwa-line system" role="status" data-testid="send-error">
          {apiErrorMessage(send.error)}
        </div>
      ) : null}
      {/* Deciding takes the screen: the composer and chips step aside while a
          decision is open, so the reason box can't be mistaken for it. */}
      {pinOpen && pinned.size > 0 ? null : <Composer
        workspaceId={workspaceId}
        chips={q.data.chips}
        draft={draft}
        setDraft={setDraft}
        placeholder={placeholder}
        trust={t(q.data.trust)}
        onSend={submit}
        sending={send.isPending}
        onBrowse={() => {
          if (!send.isPending) send.mutate({ text: t(UI.browseAgents) });
        }}
        onNewTopic={() => {
          setDraft(EMPTY);
          void chatApi.topic(workspaceId).then(() => qc.invalidateQueries({ queryKey: ["chat-thread", workspaceId] }));
        }}
      />}
    </div>
  );
}
